import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useCategories, useCategoryUrl } from '@zageno/catalog';
import { Category as CategoryApi } from '@zageno/catalog/dist/api/models';
import type { RootState, AppDispatch } from './store';
import { Category } from './api';

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useCurrentPath = (): string => {
  const pathname = () => `${window.location.pathname}${window.location.search}`;
  const [currentPath, setCurrentPath] = useState(pathname());

  const getCurrentPath = () => {
    setCurrentPath(pathname());
  };

  useEffect(() => {
    window.addEventListener('popstate', getCurrentPath);

    return () => {
      window.removeEventListener('popstate', getCurrentPath);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return currentPath;
};

export const useIsCurrentPath = (pathname: string) => {
  const currentPath = useCurrentPath();

  return currentPath === pathname;
};

export const useCategoriesAdapter = (): Category[] | null => {
  const categories = useCategories();
  const getCategoryUrl = useCategoryUrl();

  return useMemo(() => {
    if (!categories) return [];

    const buildTree = (arr: CategoryApi[], parent = '', paths: string[] = []): Category[] => {
      return arr
        .filter(item => item.parent_id === parent)
        .map(child => {
          const childrenCategories = buildTree(arr, child.id, paths.concat(child.name));
          let children: Category[] | Category[][];

          if (parent === '') {
            children = childrenCategories.reduce<Category[][]>(
              (acc, subcategory) => {
                if (!subcategory.children.length) {
                  acc[acc.length - 1].push(subcategory);
                } else {
                  acc.push([subcategory]);
                }

                return acc;
              },
              [[]],
            );
          } else {
            children = childrenCategories;
          }

          return {
            ...{
              name: child.name,
              url: getCategoryUrl(paths.concat(child.name)),
              supplier_punchout_identities: [],
              icon: child.assets?.icon_url,
            },
            children,
          };
        });
    };

    return buildTree(categories);
  }, [categories, getCategoryUrl]);
};
